import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from '../routes/ids.js';
import { TAX_RULE_CREATE_FORM } from './tax-rule-descriptors.js';
const tk = (value) => value;
const fieldId = (value) => value;
const formId = (value) => value;
const commandId = (value) => value;
function requiredValidation() {
    return {
        rules: [{ ruleId: 'required', messageKey: tk('validation.required') }],
        validateWhen: 'submit',
    };
}
function openingField(name, fieldType, options = {}) {
    return {
        id: fieldId(`field.openingCutover.${name}`),
        name,
        labelKey: tk(`openingCutover.fields.${name}`),
        helpTextKey: tk(`openingCutover.help.${name}`),
        fieldType,
        value: {
            hiddenValuePolicy: 'unregister',
            submitBehavior: options.outputOnly ? 'exclude' : 'include',
            ...(options.outputOnly ? { outputOnly: true } : {}),
            ...(options.defaultValue !== undefined ? { defaultValue: options.defaultValue } : {}),
        },
        ...(options.outputOnly ? { display: { readonlySubmission: 'exclude' } } : {}),
        ...(options.required ? { validation: requiredValidation() } : {}),
        ...(options.collection ? { collection: options.collection } : {}),
        layout: { md: options.fullWidth || fieldType === 'textarea' || fieldType === 'collection' ? 12 : 6 },
    };
}
function collectionColumn(name, fieldType, options = {}) {
    const md = options.md ?? 4;
    return {
        name,
        labelKey: tk(`openingCutover.columns.${name}`),
        helpTextKey: tk(`openingCutover.columnHelp.${name}`),
        fieldType,
        required: options.required ?? false,
        ...(options.optionSource ? { optionSource: options.optionSource } : {}),
        ...(options.staticOptions ? { options: options.staticOptions } : {}),
        ...(options.defaultValue !== undefined ? { defaultValue: options.defaultValue } : {}),
        layout: { xs: 12, sm: md === 3 ? 6 : md, md },
    };
}
/** ClientAccount is the economic owner referenced by OpeningCutover rows. */
const clientAccountOptionSource = {
    adapterId: 'api',
    operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
    valuePath: 'id',
    labelPath: 'fullName',
    descriptionPath: 'code',
    allowSearch: true,
    minimumSearchLength: 0,
};
/** PortfolioAccount is the custody/account record validated against ownerClientAccountId. */
const portfolioOptionSource = {
    adapterId: 'api',
    operationId: OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
    valuePath: 'id',
    labelPath: 'accountNumber',
    allowSearch: true,
    minimumSearchLength: 0,
    queryDependencies: [fieldId('clientAccountId')],
    queryParams: {
        clientAccountId: { fieldId: fieldId('clientAccountId') },
    },
};
const instrumentOptionSource = {
    adapterId: 'api',
    operationId: OPERATION_IDS.INSTRUMENT_LIST,
    valuePath: 'symbol',
    labelPath: 'instrumentName',
    descriptionPath: 'symbol',
    allowSearch: true,
    minimumSearchLength: 0,
};
const ledgerAccountOptionSource = {
    adapterId: 'api',
    operationId: OPERATION_IDS.CHART_OF_ACCOUNTS_LIST,
    valuePath: 'id',
    labelPath: 'name',
    descriptionPath: 'code',
    allowSearch: true,
    minimumSearchLength: 0,
};
const nprCurrencyOptions = [{ value: 'NPR', label: 'NPR' }];
const lotsCollection = {
    minItems: 1,
    addLabelKey: tk('openingCutover.collections.lots.add'),
    removeLabelKey: tk('openingCutover.collections.remove'),
    emptyStateKey: tk('openingCutover.collections.lots.empty'),
    itemLabelKey: tk('openingCutover.collections.lots.item'),
    summaryMode: 'count',
    columns: [
        collectionColumn('clientAccountId', 'select', { required: true, optionSource: clientAccountOptionSource }),
        collectionColumn('portfolioAccountId', 'select', { optionSource: portfolioOptionSource }),
        collectionColumn('instrumentSymbol', 'select', { required: true, optionSource: instrumentOptionSource }),
        collectionColumn('quantity', 'quantity', { required: true, md: 3 }),
        collectionColumn('totalCost', 'money', { required: true, md: 3 }),
        collectionColumn('acquisitionDate', 'date', { required: true, md: 3 }),
    ],
};
const cashCollection = {
    addLabelKey: tk('openingCutover.collections.cash.add'),
    removeLabelKey: tk('openingCutover.collections.remove'),
    emptyStateKey: tk('openingCutover.collections.cash.empty'),
    itemLabelKey: tk('openingCutover.collections.cash.item'),
    summaryMode: 'count',
    columns: [
        collectionColumn('clientAccountId', 'select', { required: true, optionSource: clientAccountOptionSource }),
        collectionColumn('portfolioAccountId', 'select', { optionSource: portfolioOptionSource }),
        collectionColumn('currency', 'select', {
            required: true,
            md: 3,
            staticOptions: [...nprCurrencyOptions],
            defaultValue: 'NPR',
        }),
        collectionColumn('amount', 'money', { required: true, md: 3 }),
    ],
};
const receivablesCollection = {
    addLabelKey: tk('openingCutover.collections.receivables.add'),
    removeLabelKey: tk('openingCutover.collections.remove'),
    emptyStateKey: tk('openingCutover.collections.receivables.empty'),
    itemLabelKey: tk('openingCutover.collections.receivables.item'),
    summaryMode: 'count',
    columns: [
        collectionColumn('clientAccountId', 'select', { required: true, optionSource: clientAccountOptionSource }),
        collectionColumn('portfolioAccountId', 'select', { optionSource: portfolioOptionSource }),
        collectionColumn('receivableType', 'text', { required: true }),
        collectionColumn('currency', 'select', {
            required: true,
            md: 3,
            staticOptions: [...nprCurrencyOptions],
            defaultValue: 'NPR',
        }),
        collectionColumn('amount', 'money', { required: true, md: 3 }),
        collectionColumn('dueDate', 'date', { md: 3 }),
    ],
};
const journalCollection = {
    minItems: 2,
    addLabelKey: tk('openingCutover.collections.journalLines.add'),
    removeLabelKey: tk('openingCutover.collections.remove'),
    emptyStateKey: tk('openingCutover.collections.journalLines.empty'),
    itemLabelKey: tk('openingCutover.collections.journalLines.item'),
    summaryMode: 'journalBalance',
    columns: [
        collectionColumn('ledgerAccountId', 'select', { required: true, optionSource: ledgerAccountOptionSource }),
        collectionColumn('debit', 'money', { md: 3 }),
        collectionColumn('credit', 'money', { md: 3 }),
        collectionColumn('narration', 'text', { md: 6 }),
    ],
};
const previewAction = {
    id: 'action.openingCutover.preview',
    schemaVersion: 1,
    module: MODULE_KEYS.ACCOUNTING,
    feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
    telemetryId: 'experience.action.openingCutover.preview',
    accessibilityLabelKey: tk('openingCutover.actions.preview'),
    privacyClassification: 'CONFIDENTIAL',
    availability: 'AVAILABLE',
    owner: 'accounting',
    operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_PREVIEW,
    labelKey: tk('openingCutover.actions.preview'),
    iconKey: 'preview',
    kind: 'command',
    commandId: commandId('cmd.admin.organization-opening-cutover.preview'),
    submitBehavior: 'form_submit',
    submitConfig: {
        validatesForm: true,
        requiresDirty: true,
        requiresValid: true,
        resetBehavior: 'toServerValues',
    },
    intent: 'primary',
    idempotencyRequired: true,
    successBehavior: 'NAVIGATE_BACK',
    successMessageKey: tk('openingCutover.messages.previewSuccess'),
    errorBehavior: 'SHOW_INLINE_ERROR',
    errorMessageKey: tk('common.error.unexpected'),
    order: 10,
    group: 'primary',
};
const openingInputFields = [
    openingField('cutoverDate', 'date', { required: true }),
    openingField('supersedesBatchId', 'text'),
    openingField('lots', 'collection', {
        required: true,
        defaultValue: [{}],
        collection: lotsCollection,
    }),
    openingField('cash', 'collection', { defaultValue: [], collection: cashCollection }),
    openingField('receivables', 'collection', {
        defaultValue: [],
        collection: receivablesCollection,
    }),
    openingField('journalLines', 'collection', {
        required: true,
        defaultValue: [{ debit: '', credit: '' }, { debit: '', credit: '' }],
        collection: journalCollection,
    }),
];
const openingSections = [
    {
        id: 'section.openingCutover.scope',
        titleKey: tk('openingCutover.sections.scope.title'),
        descriptionKey: tk('openingCutover.sections.scope.description'),
        fields: [fieldId('field.openingCutover.cutoverDate'), fieldId('field.openingCutover.supersedesBatchId')],
        layout: { columns: 2 },
    },
    {
        id: 'section.openingCutover.positions',
        titleKey: tk('openingCutover.sections.positions.title'),
        descriptionKey: tk('openingCutover.sections.positions.description'),
        fields: [fieldId('field.openingCutover.lots')],
    },
    {
        id: 'section.openingCutover.cash',
        titleKey: tk('openingCutover.sections.cash.title'),
        descriptionKey: tk('openingCutover.sections.cash.description'),
        fields: [fieldId('field.openingCutover.cash'), fieldId('field.openingCutover.receivables')],
    },
    {
        id: 'section.openingCutover.journal',
        titleKey: tk('openingCutover.sections.journal.title'),
        descriptionKey: tk('openingCutover.sections.journal.description'),
        fields: [fieldId('field.openingCutover.journalLines')],
    },
];
export const OPENING_CUTOVER_CREATE_FORM = {
    id: formId('form.admin.openingCutover.create'),
    schemaVersion: 1,
    module: MODULE_KEYS.ACCOUNTING,
    feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
    telemetryId: 'experience.form.openingCutover.create',
    accessibilityLabelKey: tk('page.openingCutover.title'),
    privacyClassification: 'CONFIDENTIAL',
    availability: 'AVAILABLE',
    owner: 'accounting',
    titleKey: tk('openingCutover.create.title'),
    descriptionKey: tk('openingCutover.create.description'),
    mode: 'create',
    operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_PREVIEW,
    fields: openingInputFields,
    sections: openingSections,
    actions: [previewAction],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: commandId('cmd.admin.organization-opening-cutover.preview'),
        successMessageKey: tk('openingCutover.messages.previewSuccess'),
        errorMessageKey: tk('common.error.unexpected'),
        includeFields: openingInputFields.map(field => field.id),
    },
};
export const OPENING_CUTOVER_EDIT_FORM = {
    ...OPENING_CUTOVER_CREATE_FORM,
    id: formId('form.admin.openingCutover.edit'),
    telemetryId: 'experience.form.openingCutover.edit',
    titleKey: tk('openingCutover.replace.title'),
    descriptionKey: tk('openingCutover.replace.description'),
    mode: 'edit',
};
const openingViewFields = [
    openingField('cutoverDate', 'date', { outputOnly: true }),
    openingField('status', 'text', { outputOnly: true }),
    openingField('version', 'number', { outputOnly: true }),
    openingField('manifestHash', 'text', { outputOnly: true }),
    openingField('journalEntryId', 'text', { outputOnly: true }),
    openingField('supersedesBatchId', 'text', { outputOnly: true }),
    openingField('supersededByBatchId', 'text', { outputOnly: true }),
    openingField('previewedBy', 'text', { outputOnly: true }),
    openingField('approvedBy', 'text', { outputOnly: true }),
    openingField('approvedAt', 'datetime', { outputOnly: true }),
    openingField('createdAt', 'datetime', { outputOnly: true }),
    openingField('updatedAt', 'datetime', { outputOnly: true }),
    openingField('lots', 'collection', { outputOnly: true, collection: lotsCollection }),
    openingField('cash', 'collection', { outputOnly: true, collection: cashCollection }),
    openingField('receivables', 'collection', { outputOnly: true, collection: receivablesCollection }),
    openingField('journalLines', 'collection', { outputOnly: true, collection: journalCollection }),
];
export const OPENING_CUTOVER_VIEW_FORM = {
    id: formId('form.admin.openingCutover.view'),
    schemaVersion: 1,
    module: MODULE_KEYS.ACCOUNTING,
    feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
    telemetryId: 'experience.form.openingCutover.view',
    accessibilityLabelKey: tk('page.openingCutover.title'),
    privacyClassification: 'CONFIDENTIAL',
    availability: 'READ_ONLY',
    owner: 'accounting',
    titleKey: tk('openingCutover.view.title'),
    descriptionKey: tk('openingCutover.view.description'),
    mode: 'view',
    operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_GET,
    fields: openingViewFields,
    sections: openingSections,
    actions: [],
    submission: {
        includeFields: [],
        excludeFields: openingViewFields.map(field => field.id),
    },
};
function asOutputOnly(field) {
    const { validation: _validation, ...rest } = field;
    return {
        ...rest,
        value: {
            ...field.value,
            submitBehavior: 'exclude',
            outputOnly: true,
        },
        display: { ...field.display, readonlySubmission: 'exclude' },
    };
}
export const TAX_RULE_EDIT_FORM = {
    ...TAX_RULE_CREATE_FORM,
    id: formId('form.admin.taxRule.edit'),
    telemetryId: 'experience.form.taxRule.edit',
    titleKey: tk('investment.data.taxRules.actions.createVersion'),
    mode: 'edit',
};
const taxRuleViewFields = TAX_RULE_CREATE_FORM.fields.map(asOutputOnly);
export const TAX_RULE_VIEW_FORM = {
    ...TAX_RULE_CREATE_FORM,
    id: formId('form.admin.taxRule.view'),
    telemetryId: 'experience.form.taxRule.view',
    titleKey: tk('investment.data.taxRules.actions.view'),
    mode: 'view',
    availability: 'READ_ONLY',
    operationId: OPERATION_IDS.TAX_RULE_READ,
    fields: taxRuleViewFields,
    actions: [],
    submission: {
        includeFields: [],
        excludeFields: taxRuleViewFields.map(field => field.id),
    },
};
export const ORG_ADMIN_GOVERNED_SETUP_FORM_REGISTRY = Object.freeze({
    [String(OPENING_CUTOVER_CREATE_FORM.id)]: OPENING_CUTOVER_CREATE_FORM,
    [String(OPENING_CUTOVER_EDIT_FORM.id)]: OPENING_CUTOVER_EDIT_FORM,
    [String(OPENING_CUTOVER_VIEW_FORM.id)]: OPENING_CUTOVER_VIEW_FORM,
    [String(TAX_RULE_CREATE_FORM.id)]: TAX_RULE_CREATE_FORM,
    [String(TAX_RULE_EDIT_FORM.id)]: TAX_RULE_EDIT_FORM,
    [String(TAX_RULE_VIEW_FORM.id)]: TAX_RULE_VIEW_FORM,
});
